import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Input,
	Flex,
	FlexItem,
	Box,
	Text,
	ProgressCircle,
	H3,
	Small,
} from '@bigcommerce/big-design';
import {
	AddCircleOutlineIcon,
	SearchIcon,
} from '@bigcommerce/big-design-icons';
import { useClient } from '../../../../../context/client';
import { useCurrency, useCustomerGroup } from '../../../context';
import './CustomerSearch.css';

function debounce(callback, rate) {
	let timeout = null;
	let abortController = new AbortController();

	return (...args) => {
		abortController.abort();
		clearTimeout(timeout);

		abortController = new AbortController();

		timeout = setTimeout(
			() => callback(abortController.signal, ...args),
			rate,
		);
	};
}

export default function CustomerSearch({ selectedCustomer, onSelectCustomer }) {
	const [searchResults, setSearchResults] = useState([]);
	const [searchFocus, setSearchFocus] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [keyword, setKeyword] = useState('');
	const [currency] = useCurrency();
	const client = useClient();
	const [, setCustomerGroup] = useCustomerGroup();

	const debouncedSearch = useMemo(
		() =>
			debounce(async function fetchCustomers(abortSignal, keyword) {
				try {
					setIsLoading(true);
					setSearchResults(
						keyword
							? await client.getCustomers(keyword, {
									signal: abortSignal,
							  })
							: [],
					);
				} catch (err) {
					console.error(err);
					setSearchResults([]);
				}

				if (!abortSignal.aborted) setIsLoading(false);
			}, 300),
		[],
	);

	useEffect(() => debouncedSearch(keyword), [debouncedSearch, keyword]);

	const selectCustomer = useCallback(
		(customer) => {
			setKeyword('');
			onSelectCustomer(customer);
			setCustomerGroup(customer.customer_group_id);
		},
		[onSelectCustomer],
	);

	return (
		<Box
			className="searchContainer tags-container"
			backgroundColor="secondary10"
			borderBottom="box"
		>
			<Flex alignItems="center" justifyContent="space-between">
				<FlexItem>
					<Flex alignItems="baseline">
						<H3 marginBottom="none">
							{selectedCustomer.id
								? `${selectedCustomer.first_name} ${selectedCustomer.last_name}`
								: 'New Customer'}
						</H3>
					</Flex>
				</FlexItem>
				<FlexItem>
					<Flex alignItems="center">
						<Text marginBottom="none">Currency: {currency}</Text>
						{selectedCustomer.id ? null : (
							<>
								<Text
									bold
									marginLeft="large"
									marginBottom="none"
								>
									Search Customers:
								</Text>
								<Box marginLeft="large" style={{ width: 400 }}>
									<Input
										placeholder="Customer Name"
										className="searchInput"
										type="text"
										iconLeft={<SearchIcon />}
										iconRight={
											isLoading && (
												<ProgressCircle size="xxSmall" />
											)
										}
										onChange={(event) =>
											setKeyword(
												event.currentTarget.value,
											)
										}
										onBlur={() => {
											// close after timeout both to make it look nice, and to give click event time to fire.
											setTimeout(() => {
												setSearchFocus(false);
											}, 100);
										}}
										value={keyword}
										onFocus={() => setSearchFocus(true)}
										autoComplete="off"
									/>
									{!isLoading &&
										searchResults.length > 0 &&
										searchFocus && (
											<Box
												className="searchResults"
												borderRadius="normal"
												border="box"
												backgroundColor="white"
												marginTop="small"
											>
												{searchResults.map(
													(result, index) => (
														<Flex
															key={index}
															alignItems="center"
															flexWrap="nowrap"
															className="searchResultItem"
															onClick={() =>
																selectCustomer(
																	result,
																)
															}
														>
															<FlexItem
																flexGrow={0}
																flexShrink={1}
																paddingLeft="small"
															>
																<AddCircleOutlineIcon />
															</FlexItem>
															<FlexItem
																flexGrow={1}
															>
																<Flex
																	flexDirection="column"
																	padding="medium"
																	margin="xxSmall"
																	borderRadius="normal"
																>
																	<FlexItem marginBottom="xxSmall">
																		<Text
																			bold
																		>
																			{
																				result.first_name
																			}{' '}
																			{
																				result.last_name
																			}
																		</Text>
																	</FlexItem>
																	<FlexItem
																		flexGrow={
																			1
																		}
																	>
																		<Box marginBottom="xxSmall">
																			{(result.email && (
																				<Text>
																					{
																						result.email
																					}
																				</Text>
																			)) || (
																				<Small>
																					Customer
																					has
																					no
																					saved
																					email
																					address
																				</Small>
																			)}
																		</Box>
																		<Box marginBottom="xxSmall">
																			{(result.phone && (
																				<Text>
																					{
																						result.phone
																					}
																				</Text>
																			)) || (
																				<Small>
																					Customer
																					has
																					no
																					saved
																					phone
																					number
																				</Small>
																			)}
																		</Box>
																		{result.address_count ===
																			0 && (
																			<Small>
																				Custom
																				has
																				no
																				saved
																				addressed
																			</Small>
																		)}
																	</FlexItem>
																</Flex>
															</FlexItem>
														</Flex>
													),
												)}
											</Box>
										)}
								</Box>
							</>
						)}
					</Flex>
				</FlexItem>
			</Flex>
		</Box>
	);
}
